import { Investors } from '@components/book-trial-class-jp/investors'
import CodingCertificate from '@components/common/coding-certificate'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import ContentCard from '@components/course-landing-page/content-card'
import CourseDescription from '@components/course-landing-page/course-description'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import { COURSE_NAMES, FAQ } from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { ThunkableSeo } from '@lib/seo'
import { Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'

const CourseCard = dynamic(() =>
  import('@components/course-landing-page/course-card')
)

const { THUNKABLE } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['ad'].trialCourseQuery}`

const Thunkable = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  useEffect(() => {
    const handleScroll = () => setShouldUpdateCourseCard(window.scrollY > 600)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useStoreUTMParams()

  return (
    <>
      <NextSeo {...ThunkableSeo} />
      <FAQPageJsonLd
        mainEntity={FAQ[THUNKABLE].map(
          ({ acceptedAnswerText, questionName }) => ({
            acceptedAnswerText: acceptedAnswerText,
            questionName: questionName,
          })
        )}
      />
      <CourseJsonLd
        courseName="Top Coding Class Instructors for Kids and Teens to Learn Thunkable"
        description="At Codingal, our K-12-aligned curriculum introduces students to advanced application development using the block-based platform, Thunkable. Through this course, students explore exciting concepts like integrating sensors (e.g., accelerometers to tilt the phone and move characters like in Subway Surfers or Temple Run), location-based sensors, and screen design. They also delve into AI, including text-to-speech converters, language translators (like Google Translate), and database integration with Firebase or Google Sheets. With Firebase integration, students can create apps with real-time data storage, while Google Sheets allows for more lightweight database management. Students will learn to predict weather using Thunkable, create a health app to calculate BMI, and even design a cool calculator. They'll develop skills to build a barcode and QR code scanner, an image recognizer that converts objects to text, and a messenger app to chat with friends in real-time. This course merges creativity, coding, and practical applications to enhance learning!
"
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT44H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        shouldHideCourseCard={shouldUpdateCourseCard}
        courseName={THUNKABLE}
        urlQuery={urlQuery}
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={THUNKABLE} />
          <RecommendedCourses
            courseName={THUNKABLE}
            courseCodes={['ad']}
            title="Our Thunkable class curriculum"
            description={
              <Trans>
                In our Application Development for Kids course, students
                kickstart their app development skills by building fun and
                practical apps. They begin by creating a PDF Reader to display
                documents, a Space Warriors game to learn basic game mechanics,
                and a Check-List App to help users manage tasks. These projects
                introduce essential components such as buttons, images, text
                fields, and logic blocks, teaching students how to design
                functional and interactive apps. As they progress, students
                tackle more advanced apps like the Mole Mash game to explore
                animation and user interaction, a Weather App to fetch real-time
                data, and a Barcode Scanner app to scan and display information.
                These projects help solidify their understanding of app logic,
                sensors, and APIs, setting the stage for even more complex app
                development.
                <br />
                They also start exploring AI-powered features, such as
                integrating speech recognition or text-to-speech functions,
                allowing their apps to respond to voice commands or convert text
                into spoken words. These projects build a strong foundation in
                coding, sensors, APIs, and AI, preparing students for even more
                advanced app development.
              </Trans>
            }
          />
          <Activities courseName={THUNKABLE} />
          <Teachers
            urlQuery={urlQuery}
            title={
              <Trans>
                Learn from experienced teachers. Our hand-picked coding tutors
                ensure the best learning experience
              </Trans>
            }
            description={
              <Trans>
                All our instructors have a background in Computer Science and
                undergo rigorous training, contributing to our outstanding NPS
                score of 86. Teachers receive a rating of 4.9 out of 5 from
                students and parents, while the curriculum content is rated 4.8
                out of 5. With a teacher ratio of 91% female to 9% male, we
                provide a comfortable learning environment. Our tutors
                understand students&#39; needs and offer personalized attention,
                even in group classes. Additionally, students have access to
                24/7 doubt resolution with an expert tutor whenever they need
                assistance.
              </Trans>
            }
          />
          <CourseFeatures
            courseTitle={THUNKABLE}
            title={
              <Trans>An engaging class for kids to build mobile apps</Trans>
            }
          />

          <ContentCard
            title={
              <Trans>
                The best place for kids to learn the basics and fundamentals
              </Trans>
            }
            courseName={THUNKABLE}
          />

          <StemAccredited
            urlQuery={urlQuery}
            heading="STEM.org accredited educational experience and certificates"
            description="At Codingal, we are proud to be a STEM.org-accredited coding school, utilizing scientific teaching methods based on Bloom's Taxonomy, BIDE, and STEAM principles. Our curriculum is meticulously aligned with Common Core, state standards, and Advanced Placement (AP) curricula, ensuring students receive a comprehensive and relevant education. We also cover the K-12 Computer Science Framework, providing developmentally appropriate content that equips learners with essential skills for a technology-driven world."
          />
          <CodingCertificate
            title="Unlock your skills and earn your App Development certifications"
            description={
              <>
                In the App Development for Kids course, learners from grades 4
                to 8 can earn STEM.org-certified coding certificates—App Creator
                and App Innovator—that celebrate their achievements. Using
                Thunkable as the platform, students progress from building
                simple apps to creating their own cool, fully functional
                projects!
                <br />
                As they advance through the course, students unlock special
                badges and leaderboard rankings, making app development a fun
                and rewarding experience. These certifications are easy to
                download and share, are STEM.org accredited, and can be added to
                your child&#39;s learning portfolio to showcase their
                accomplishments to the world and on social media.
              </>
            }
            imagePath="/images/courses/ai-and-ml/cetrificate"
            imageWidth={230}
            imageHeight={195}
          />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
          />
          <TrustpilotReview shouldScrollToTop isCoursepage />

          <CodingForStudents
            title="Our Thunkable classes are appropriate for high school, middle school, and elementary school students."
            description={
              <>
                Our Thunkable course classes are designed for young learners in
                grades 4-8 (ages 9-14), helping them enhance their academic
                skills as they learn both block-based and text-based coding.
                <br />
                Codingal supports kids aged 6 to 18 and grades K to 12,
                providing engaging and comprehensive coding education that
                fosters creativity and critical thinking skills. We create a
                dynamic learning environment that empowers students to explore
                the exciting worlds of coding and artificial intelligence.
              </>
            }
            imageWidth={235}
            imageHeight={241}
            imagePath="/images/courses/ai-and-ml/ai-course-students"
          />
          <Faq courseName={THUNKABLE} title="Frequently Asked Questions" />
          <Investors isCoursePage urlQuery={urlQuery} />
          <RecommendedCourses courseCodes={['aigc', 'rc', 'sp', 'gd']} />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={THUNKABLE}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <FooterBanner
        title="Thunkable for kids with the best Computer Science teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default Thunkable
